import {
  Component,
  Directive,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  Injector,
  Renderer2,
  ComponentRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  ComponentFactoryResolver,
  NgZone
} from '@angular/core';
import { listenToTriggers } from '../util/triggers';
import { positionElements } from '../util/positioning';
import { PopupService } from '../util/popup';
import { FishTooltipConfig } from './tooltip-config';

let nextId = 0;


export class FishTooltipWindow {
   placement: 'top' | 'bottom' | 'left' | 'right' = 'top';
   id: string;
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'fish-tooltip-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': '"tooltip show tooltip-" + placement', 'role': 'tooltip', '[id]': 'id' },
  template: `
    <div class="tooltip-inner"><ng-content></ng-content></div>
    `
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'placement': [{ type: Input },],
'id': [{ type: Input },],
};
}

/**
 * A lightweight, extensible directive for fancy tooltip creation.
 */

export class FishTooltip implements OnInit, OnDestroy {
  /**
   * Placement of a tooltip. Accepts: "top", "bottom", "left", "right"
   */
   placement: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Specifies events that should trigger. Supports a space separated list of event names.
   */
   triggers: string;
  /**
   * A selector specifying the element the tooltip should be appended to.
   * Currently only supports "body".
   */
   container: string;
  /**
   * Emits an event when the tooltip is shown
   */
   shown = new EventEmitter();
  /**
   * Emits an event when the tooltip is hidden
   */
   hidden = new EventEmitter();

  private _fishTooltip: string | TemplateRef<any>;
  private _fishTooltipWindowId = `fish-tooltip-${nextId++}`;
  private _popupService: PopupService<FishTooltipWindow>;
  private _windowRef: ComponentRef<FishTooltipWindow>;
  private _unregisterListenersFn;
  private _zoneSubscription: any;

  constructor(
    private _elementRef: ElementRef, private _renderer: Renderer2, injector: Injector,
    componentFactoryResolver: ComponentFactoryResolver, viewContainerRef: ViewContainerRef, config: FishTooltipConfig,
    ngZone: NgZone) {
    this.placement = config.placement;
    this.triggers = config.triggers;
    this.container = config.container;
    this._popupService = new PopupService<FishTooltipWindow>(
      FishTooltipWindow, injector, viewContainerRef, _renderer, componentFactoryResolver);

    this._zoneSubscription = ngZone.onStable.subscribe(() => {
      if (this._windowRef) {
        positionElements(
          this._elementRef.nativeElement, this._windowRef.location.nativeElement, this.placement,
          this.container === 'body');
      }
    });
  }

  /**
   * Content to be displayed as tooltip. If falsy, the tooltip won't open.
   */
  
  set fishTooltip(value: string | TemplateRef<any>) {
    this._fishTooltip = value;
    if (!value && this._windowRef) {
      this.close();
    }
  }

  get fishTooltip() { return this._fishTooltip; }

  /**
   * Opens an element’s tooltip. This is considered a “manual” triggering of the tooltip.
   * The context is an optional value to be injected into the tooltip template when it is created.
   */
  open(context?: any) {
    if (!this._windowRef && this._fishTooltip) {
      this._windowRef = this._popupService.open(this._fishTooltip, context);
      this._windowRef.instance.placement = this.placement;
      this._windowRef.instance.id = this._fishTooltipWindowId;

      this._renderer.setAttribute(this._elementRef.nativeElement, 'aria-describedby', this._fishTooltipWindowId);

      if (this.container === 'body') {
        window.document.querySelector(this.container).appendChild(this._windowRef.location.nativeElement);
      }

      // we need to manually invoke change detection since events registered via
      // Renderer::listen() - to be determined if this is a bug in the Angular itself
      this._windowRef.changeDetectorRef.markForCheck();
      this.shown.emit();
    }
  }

  /**
   * Closes an element’s tooltip. This is considered a “manual” triggering of the tooltip.
   */
  close(): void {
    if (this._windowRef != null) {
      this._renderer.removeAttribute(this._elementRef.nativeElement, 'aria-describedby');
      this._popupService.close();
      this._windowRef = null;
      this.hidden.emit();
    }
  }

  /**
   * Toggles an element’s tooltip. This is considered a “manual” triggering of the tooltip.
   */
  toggle(): void {
    if (this._windowRef) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Returns whether or not the tooltip is currently being shown
   */
  isOpen(): boolean { return this._windowRef != null; }

  ngOnInit() {
    this._unregisterListenersFn = listenToTriggers(
      this._renderer, this._elementRef.nativeElement, this.triggers, this.open.bind(this), this.close.bind(this),
      this.toggle.bind(this));
  }

  ngOnDestroy() {
    this.close();
    this._unregisterListenersFn();
    this._zoneSubscription.unsubscribe();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[fishTooltip]',
  exportAs: 'fishTooltip'
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: Injector, },
{type: ComponentFactoryResolver, },
{type: ViewContainerRef, },
{type: FishTooltipConfig, },
{type: NgZone, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'placement': [{ type: Input },],
'triggers': [{ type: Input },],
'container': [{ type: Input },],
'shown': [{ type: Output },],
'hidden': [{ type: Output },],
'fishTooltip': [{ type: Input },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
